import { useEffect, useRef, useState, forwardRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import HTMLFlipBook from "react-pageflip";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

// Worker hosted on unpkg, version-pinned to the pdfjs-dist build shipped with react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Each "page" of the flipbook — react-pageflip needs forwardRef on its direct children
const FlipPage = forwardRef(({ pageNumber, width, height }, ref) => (
  <div
    ref={ref}
    className="bg-white shadow-inner"
    style={{ width, height }}
    data-testid={`flipbook-page-${pageNumber}`}
  >
    <Page
      pageNumber={pageNumber}
      width={width}
      renderAnnotationLayer={false}
      renderTextLayer={false}
      loading={
        <div className="flex items-center justify-center h-full text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      }
    />
  </div>
));
FlipPage.displayName = "FlipPage";

const FlipbookViewer = ({ pdfUrl }) => {
  const bookRef = useRef(null);
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  // Default to landscape 16:9 until the actual PDF aspect ratio is detected
  const [pageRatio, setPageRatio] = useState(9 / 16); // height / width
  const [dims, setDims] = useState({ width: 480, height: 270 });
  const [loadError, setLoadError] = useState(null);

  // Compute responsive page size from container width + detected aspect ratio
  const [usePortrait, setUsePortrait] = useState(true);
  useEffect(() => {
    const compute = () => {
      const el = containerRef.current;
      if (!el) return;
      const cw = el.clientWidth;
      const isLandscape = pageRatio < 1;
      const portraitMode = cw < 820; // single-page below 820px
      setUsePortrait(portraitMode);
      // When double-spread, divide width by 2; when single, allow a roomier max
      const maxPerPage = isLandscape ? 600 : 480;
      const targetPageW = portraitMode
        ? Math.min(maxPerPage + 60, cw - 24)
        : Math.min(maxPerPage, (cw - 48) / 2);
      const targetPageH = Math.round(targetPageW * pageRatio);
      setDims({ width: Math.round(targetPageW), height: targetPageH });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [pageRatio]);

  const onDocLoad = async (pdf) => {
    setNumPages(pdf.numPages);
    try {
      const firstPage = await pdf.getPage(1);
      const vp = firstPage.getViewport({ scale: 1 });
      if (vp.width > 0) setPageRatio(vp.height / vp.width);
    } catch (_) { /* keep default ratio */ }
  };

  const goPrev = () => bookRef.current?.pageFlip()?.flipPrev();
  const goNext = () => bookRef.current?.pageFlip()?.flipNext();

  return (
    <div ref={containerRef} className="w-full" data-testid="flipbook-viewer">
      <Document
        file={pdfUrl}
        onLoadSuccess={onDocLoad}
        onLoadError={(err) => setLoadError(err?.message || "Failed to load PDF")}
        loading={
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading welcome packet…
          </div>
        }
        error={
          <div className="text-center py-16 text-rose-600" data-testid="flipbook-error">
            Unable to load PDF. {loadError}
          </div>
        }
      >
        {numPages > 0 && (
          <>
            <div className="flex justify-center">
              <HTMLFlipBook
                ref={bookRef}
                width={dims.width}
                height={dims.height}
                size="fixed"
                minWidth={280}
                maxWidth={680}
                minHeight={200}
                maxHeight={900}
                showCover={true}
                mobileScrollSupport={true}
                drawShadow={true}
                flippingTime={650}
                maxShadowOpacity={0.4}
                usePortrait={usePortrait}
                onFlip={(e) => setCurrentPage(e.data)}
                className="flipbook-shadow"
                style={{}}
                startPage={0}
                startZIndex={0}
                autoSize={false}
                clickEventForward={true}
                useMouseEvents={true}
                swipeDistance={30}
                showPageCorners={true}
                disableFlipByClick={false}
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <FlipPage
                    key={i + 1}
                    pageNumber={i + 1}
                    width={dims.width}
                    height={dims.height}
                  />
                ))}
              </HTMLFlipBook>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={goPrev}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition shadow-sm disabled:opacity-40"
                disabled={currentPage === 0}
                data-testid="flipbook-prev-btn"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-sm font-medium text-slate-600 tabular-nums" data-testid="flipbook-page-counter">
                Page {Math.min(currentPage + 1, numPages)} of {numPages}
              </span>
              <button
                onClick={goNext}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-navy text-white hover:bg-brand-navy/90 transition shadow-sm disabled:opacity-40"
                disabled={currentPage >= numPages - 1}
                data-testid="flipbook-next-btn"
                aria-label="Next page"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-400 text-center mt-3">
              Tip: click or drag the page corner to turn pages.
            </p>
          </>
        )}
      </Document>
    </div>
  );
};

export default FlipbookViewer;
